import { useMemo, useState } from 'react';
import type { CallSheetMembershipDto, ProspectBookRowDto } from '@signalvote/shared';
import { Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { SelectCheckbox } from './SelectCheckbox';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { booksByPerson, planCallSheetAdd, type SkipReason } from '@/lib/fundraising/callSheetAdd';

const count = new Intl.NumberFormat('en-US');
const people = (n: number) => `${count.format(n)} ${n === 1 ? 'person' : 'people'}`;
const books = (n: number) => `${count.format(n)} ${n === 1 ? 'book' : 'books'}`;

/** A few names, then how many more: "Ann Lee, Bo Cho and 4 more". */
function names(rows: ProspectBookRowDto[], shown = 3): string {
  const listed = rows.slice(0, shown).map((row) => row.prospect.name);
  const more = rows.length - listed.length;
  if (more > 0) return `${listed.join(', ')} and ${count.format(more)} more`;
  if (listed.length <= 1) return listed.join('');
  return `${listed.slice(0, -1).join(', ')} and ${listed[listed.length - 1]}`;
}

const SKIP_LABEL: Record<SkipReason, string> = {
  doNotCall: 'Do Not Call',
  alreadyOn: 'Already on it',
  noPhone: 'No phone',
};

const NO_BOOKS = new Map<string, CallSheetMembershipDto[]>();

/** How many "also on another book" sentences to spell out before summing up the rest. */
const ALSO_ON_SHOWN = 3;

/**
 * "Add N people to call books": tick any number of books, or start a new one, and see before confirming who
 * will be added, who will be skipped and why (by name), and who is also on another book — allowed, and
 * said. Worked out from the book read; the server checks again when the add lands.
 */
export function AddToCallSheetDialog({
  open,
  onOpenChange,
  selected,
  books: allBooks,
  onAdd,
  onCreate,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Everyone selected, as last seen. */
  selected: ProspectBookRowDto[];
  books: CallSheetMembershipDto[];
  onAdd: (book: CallSheetMembershipDto, prospectIds: string[]) => void;
  onCreate: (name: string, prospectIds: string[]) => void;
}) {
  const [ticked, setTicked] = useState<Set<string>>(() => new Set());
  const [newName, setNewName] = useState<string | null>(null);
  const creating = newName !== null && newName.trim() !== '';
  // Everyone's books, once per read of the books; the plan once per change to what it depends on — the selection,
  // the books, the ticks and whether a new book is being started, never the letters of its name.
  // Nothing is worked out while the dialog is closed, however the selection changes behind it.
  const onBooks = useMemo(() => (open ? booksByPerson(allBooks) : NO_BOOKS), [open, allBooks]);
  const plan = useMemo(
    () => planCallSheetAdd(open ? selected : [], allBooks, ticked, creating, onBooks),
    [open, selected, allBooks, ticked, creating, onBooks],
  );
  const skippedCount = plan.skipped.doNotCall.length + plan.skipped.alreadyOn.length + plan.skipped.noPhone.length;
  const anyChoice = ticked.size > 0 || creating;
  const addedCount = plan.addedPeople.length;

  const reset = () => {
    setTicked(new Set());
    setNewName(null);
  };
  const toggle = (id: string) =>
    setTicked((was) => {
      const next = new Set(was);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const close = () => {
    reset();
    onOpenChange(false);
  };

  const confirm = () => {
    for (const add of plan.adds) {
      if (add.book) onAdd(add.book, add.prospectIds);
      else onCreate(newName!.trim(), add.prospectIds);
    }
    close();
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) reset();
        onOpenChange(next);
      }}
    >
      <DialogContent className="max-w-[420px] gap-0 overflow-hidden p-0">
        <DialogTitle className="px-4 pb-2 pt-4 text-base font-semibold">Add {people(selected.length)} to call books</DialogTitle>
        <DialogDescription className="sr-only">
          Tick the call books to add them to. Who will be skipped, and why, shows before you confirm.
        </DialogDescription>
        <div className="flex max-h-64 flex-col overflow-y-auto px-2 pb-2">
          {allBooks.map((book) => (
            <label
              key={book.id}
              data-book={book.id}
              className="flex min-h-11 cursor-pointer items-center gap-3 rounded-lg px-2 hover:bg-muted"
            >
              <SelectCheckbox checked={ticked.has(book.id)} onCheckedChange={() => toggle(book.id)} aria-label={book.name} />
              <span className="flex-1 truncate text-[13px] font-medium">{book.name}</span>
              <span className="text-xs tabular-nums text-muted-foreground">{people(book.size)}</span>
            </label>
          ))}
          {newName === null ? (
            <button
              type="button"
              onClick={() => setNewName('')}
              className="flex min-h-11 items-center gap-3 rounded-lg px-2 text-left text-[13px] font-medium text-muted-foreground hover:bg-muted"
            >
              <Plus className="h-4 w-4" aria-hidden="true" />
              New call book…
            </button>
          ) : (
            <div className="flex min-h-11 items-center gap-3 px-2">
              <Plus className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
              <Input
                autoFocus
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder="New call book name"
                aria-label="New call book name"
                className="h-9 text-[13px]"
              />
            </div>
          )}
        </div>
        {anyChoice && (
          <div role="status" className="space-y-1.5 border-t bg-muted px-4 py-3">
            <p className="text-[13px] leading-5">
              <span className="font-semibold">Adds {people(addedCount)}</span> to {books(plan.bookCount)}.
              {skippedCount > 0 && <span className="font-semibold"> Skips {count.format(skippedCount)}:</span>}
            </p>
            {skippedCount > 0 && (
              <dl className="grid grid-cols-[110px_minmax(0,1fr)] gap-y-0.5 text-xs leading-4">
                {(Object.keys(SKIP_LABEL) as SkipReason[])
                  .filter((reason) => plan.skipped[reason].length > 0)
                  .map((reason) => (
                    <div key={reason} className="contents">
                      <dt className="font-semibold">{SKIP_LABEL[reason]}</dt>
                      <dd className="text-muted-foreground">{names(plan.skipped[reason])}</dd>
                    </div>
                  ))}
              </dl>
            )}
            {plan.alsoOn.slice(0, ALSO_ON_SHOWN).map(({ person, books: on }) => (
              <p key={person.prospect.id} className="text-xs leading-4 text-muted-foreground">
                {person.prospect.name} is also on {on.join(', ')}. That’s allowed.
              </p>
            ))}
            {plan.alsoOn.length > ALSO_ON_SHOWN && (
              <p className="text-xs leading-4 text-muted-foreground">
                {people(plan.alsoOn.length - ALSO_ON_SHOWN)} more {plan.alsoOn.length - ALSO_ON_SHOWN === 1 ? 'is' : 'are'} also on
                another book.
              </p>
            )}
          </div>
        )}
        <div className="flex justify-end gap-2 px-4 py-3">
          <Button variant="ghost" onClick={close}>
            Cancel
          </Button>
          <Button disabled={addedCount === 0} onClick={confirm}>
            Add {count.format(addedCount)} to {books(plan.bookCount)}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
